import { useRef, useEffect } from "react";
import { useComputed, useSignal } from "@preact/signals-react";
import { Box } from "@mui/material";
import { INGChartProps } from "../../library/NGFieldExtensions";
import { setupLocalState, GetFormatFromSite, getLocale, getDefaultCCY } from "../../library/dataService";
import { getTestId, formatDate, formatNumber } from "../../library/utils";
import { getTypename } from "../../library/metadataUtils";
import type { AgCharts as AgChartsType } from "ag-charts-react";
import { agLicense } from "../../loaders/moduleLoader";
import { AgCartesianAxisOptions, AxisOptions } from "ag-charts-enterprise";


export default function NGAGChart({ config, context }: INGChartProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const local = setupLocalState(
    config,
    {
      Rows: useSignal(config.Rows ?? []),
      TransposeRowsToSeries: useSignal(config.TransposeRowsToSeries ?? false),
      TransposeRowNames: useSignal(config.TransposeRowNames ?? ""),
      TransposeRowValueFrom: useSignal(config.TransposeRowValueFrom ?? ""),
      TransposeRowLabel: useSignal(config.TransposeRowLabel ?? undefined),
      Loading: useSignal(config.Loading ?? false),
      ChartMetadata: useSignal(config.ChartMetadata ?? {}),
      Visible: useSignal(config.Visible ?? true),
      Colors: useSignal(config.Colors ?? []),
      XAxisFormatter: useSignal(config.XAxisFormatter ?? ""),
      YAxisFormatter: useSignal(config.YAxisFormatter ?? ""),
      Height: useSignal(config.Height ?? null),
      Zoom: useSignal(config.Zoom ?? false),
      ZoomNavigator: useSignal(config.ZoomNavigator ?? false),
      CarriageReturnOption: useSignal(config.CarriageReturnOption ?? ""),
      EllipsisOption: useSignal(config.EllipsisOption ?? ""),
      PaddingInner: useSignal(config.PaddingInner ?? null),
      PaddingOuter: useSignal(config.PaddingOuter ?? null),
      GroupPaddingInner: useSignal(config.GroupPaddingInner ?? null),
    },
    context
  );

  const agChartsModule = useSignal<typeof AgChartsType | null>(null);
  useEffect(() => {
    const loadModules = async () => {
      const agChartsEnterprise = await import("ag-charts-enterprise");
      const module = await import("ag-charts-react");
      agChartsModule.value = module.AgCharts;
      agChartsEnterprise.AgCharts.setLicenseKey(agLicense);
    };

    loadModules();
  }, []);
  const AgCharts = agChartsModule.value;

  function chooseColorBasedOnName(name?: string) {
    const baseColors = ["#EC2500", "#ECE100", "#EC9800", "#9EDE00", "#00C0EF", "#00ECB4", "#0099EC"];
    if (!name) return baseColors[0]
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return baseColors[Math.abs(hash) % baseColors.length];
  }

  const colorCache = new Map<string, string>();
  function resolveCssVar(input: string, rootEl: HTMLElement | null): string {
    if (!input?.startsWith("var(")) return input;

    // Matches: var(--name) or var(--name, fallback)
    const m = /^var\(\s*([^,\s)]+)\s*(?:,\s*(.+?)\s*)?\)$/.exec(input);
    if (!m) return input;

    const varName = m[1]; // --md-sys-color-positive
    const fallback = m[2]; // optional
    const scope = rootEl ?? (typeof document !== "undefined" ? document.documentElement : null);
    const resolved = scope ? getComputedStyle(scope).getPropertyValue(varName).trim() : "";

    if (resolved) return resolved;
    return fallback ? resolveCssVar(fallback, rootEl) : input;
  }

  function normalizeCssColor(input?: string, rootEl?: HTMLElement | null): string | undefined {
    if (!input) return undefined;

    const key = `${input}@@${rootEl ? "scoped" : "global"}`;
    if (colorCache.has(key)) return colorCache.get(key)!;

    // SSR guard
    if (typeof window === "undefined" || typeof document === "undefined") {
      colorCache.set(key, input);
      return input;
    }

    const resolved = input.startsWith("var(") ? resolveCssVar(input, rootEl ?? null) : input;

    // Let the browser parse/normalize any color string
    const el = document.createElement("span");
    el.style.color = "";
    el.style.color = resolved;
    document.body.appendChild(el);
    const out = getComputedStyle(el).color || resolved;
    el.remove();

    colorCache.set(key, out);
    return out;
  }

  function applyEllipsis(value: any, maxChars = 20): string {
    const text = String(value);
    return text.length > maxChars ? text.slice(0, maxChars) + "…" : text;
  }

  function applyCarriageReturn(value: any, maxChars = 15): string {
    const words = String(value).split(" ");
    let line = "";
    const lines: string[] = [];

    words.forEach((word) => {
      if ((line + word).length > maxChars) {
        lines.push(line.trim());
        line = word + " ";
      } else {
        line += word + " ";
      }
    });

    if (line) lines.push(line.trim());
    return lines.join("\n");
  }

  function composeFormatter(formatFn?: (value: any) => string, options?: { ellipsis?: number; carriage?: boolean }) {
    return (params: any) => {
      let value = params.value;

      if (formatFn) {
        value = formatFn(value);
      }

      if (options?.carriage) {
        value = applyCarriageReturn(value);
      }

      if (options?.ellipsis) {
        value = applyEllipsis(value, options.ellipsis);
      }

      return value;
    };
  }

  const options = useComputed(() => {
    const ccy = getDefaultCCY();
    const result: any = { ...local.ChartMetadata.value };

    if (local.Rows.value?.length > 0) {
      result.data = local.Rows.value;
    }
    if (local.Height.value) {
      result.chart.height = null;
    }

    const colors = local.Colors.value;

    if (local.Zoom.value) {
      result.zoom = {
        enabled: true,
      };
    }

    if (local.ZoomNavigator.value) {
      result.navigator = {
        enabled: true,
        miniChart: {
          enabled: true,
        },
      };
    }

    result.series?.forEach((s: any, i: number) => {
      const xKey = s.xKey;
      const colorMap = new Map<string, string>();
      result?.data?.forEach?.((d: any, i: number) => {
        const keyValue = d[xKey];
        if (keyValue !== undefined) {
          colorMap.set(keyValue, colors[i % colors.length]);
        }
      });
      const declaredYKey = s.yKey || (Array.isArray(s.yKeys) ? s.yKeys[0] : "");
      const baseColorRaw = colors?.[i] ?? chooseColorBasedOnName(declaredYKey);
      s.fill = s.fill ?? baseColorRaw;
      s.stroke = s.stroke ?? baseColorRaw;

      let format: any = null;
      if (config.YAxisFormatter) {
        format = GetFormatFromSite(config.YAxisFormatter);
      }
      const bindings = format?.ConditionalFormatBindings;
      const formats = format?.ConditionalFormats;

      if (bindings && formats) {
        const POS =
          normalizeCssColor(formats.Positive?.Style?.color, rootRef.current) ?? "var(--md-sys-color-positive)";
        const NEG =
          normalizeCssColor(formats.Negative?.Style?.color, rootRef.current) ?? "var(--md-sys-color-negative)";
        const ZERO = normalizeCssColor(formats.Zero?.Style?.color, rootRef.current) ?? "var(--md-sys-color-zero)";
        const BASE = normalizeCssColor(baseColorRaw, rootRef.current) ?? normalizeCssColor(s.fill, rootRef.current) ?? baseColorRaw;

        s.itemStyler = (params: any) => {
          const { datum, yKey } = params;
          const value = datum?.[yKey as string];
          let fill = colorMap.get(datum?.[xKey]) ?? BASE;
          if (typeof value === "number") {
            if (bindings.Positive && value > 0) {
              fill = POS;
            } else if (bindings.Negative && value < 0) {
              fill = NEG;
            } else if (bindings.Zero && value === 0) {
              fill = ZERO;
            }
          }

          return {
            fill,
          };
        };
      } else {
        s.itemStyler = (params: any) => {
          const { datum } = params;
          const fill = colorMap.get(datum?.[xKey]) ?? normalizeCssColor(s.fill, rootRef.current) ?? normalizeCssColor(baseColorRaw, rootRef.current) ?? baseColorRaw;
          return {
            fill,
            stroke: fill
          };
        };
      }
    });

    if (Array.isArray(result.series) && result.series.length > 0) {
      const { xKey, yKey } = result.series[0] || {};
      const axes: any[] = [];

      // X Axis
      if (xKey) {
        const xAxisConfig: any = { type: "category", position: "bottom", key: xKey };
        let baseFormatter: ((value: any) => string) | undefined;

        if (config.XAxisFormatter) {
          const format = GetFormatFromSite(config.XAxisFormatter);

          if (format?.DateFormat) {
            baseFormatter = (value: any) => {
              const date = typeof value === "number" || typeof value === "string" ? new Date(value) : null;
              if (date && !isNaN(date.getTime())) {
                return formatDate(getLocale(), date.toISOString(), format.DateFormat);
              }
              return value;
            };
          } else if (format?.NumberFormat) {
            baseFormatter = (value: any) => formatNumber(getLocale(), ccy, value, format?.NumberFormat);
          }
        }

        xAxisConfig.label = {
          formatter: composeFormatter(baseFormatter, {
            ellipsis: local.EllipsisOption.value ? Number(local.EllipsisOption.value) : undefined,
            carriage: !!local.CarriageReturnOption.value,
          }),
        };

        xAxisConfig.tooltip = { enabled: true };

        if (local.PaddingInner?.value !== undefined) {
          xAxisConfig.paddingInner = Number(local.PaddingInner?.value);
        }
        if (local.PaddingOuter?.value !== undefined) {
          xAxisConfig.paddingOuter = Number(local.PaddingOuter?.value);
        }
        if (local.GroupPaddingInner?.value !== undefined) {
          xAxisConfig.groupPaddingInner = Number(local.GroupPaddingInner?.value);
        }
        axes.push(xAxisConfig);
      }

      // Y Axis
      if (yKey) {
        const metadataTAxis = result?.axes?.find((a: any) => a.position === "left" || a.position === "right");
        const yAxisConfig: AgCartesianAxisOptions = {
          type: "number", position: "left", key: yKey, ...metadataTAxis
        };
        if (config.YAxisFormatter) {
          const format = GetFormatFromSite(config.YAxisFormatter);
          if (format?.DateFormat) {
            yAxisConfig.label = {
              ...metadataTAxis?.label,
              formatter: (params: any) => {
                const value: any = params.value;
                const date = typeof value === "number" || typeof value === "string" ? new Date(value) : null;
                if (date && !isNaN(date.getTime())) {
                  return formatDate(getLocale(), date.toISOString(), format.DateFormat);
                }
                return value;
              },
            };
          } else if (format?.NumberFormat) {
            yAxisConfig.label = {
              ...metadataTAxis?.label,
              formatter: (params: any) => {
                return formatNumber(getLocale(), ccy, params.value, format?.NumberFormat);
              },
            };
          }
        }
        axes.push(yAxisConfig);
      }

      if (axes.length > 0) {
        result.axes = axes;
      }
    }

    return result;
  });

  return (
    AgCharts && (
      <>
        {local.Loading.value && <Box>Loading...</Box>}
        {local.Visible.value && (
          <Box
            ref={rootRef}
            data-testid={getTestId(config)}
            data-type={getTypename(config)}
            sx={{
              ...(local.Height.value ? { height: local.Height.value } : {})
            }}
          >
            <AgCharts options={options.value} />
          </Box>
        )}
      </>
    )
  );
}
